import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, RotateCcw, Eye, Check, Copy } from "lucide-react";

const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}` });

// Contexte d'exemple pour l'aperçu
const SAMPLE_CONTEXT = {
  prenom: "Marie", nom: "Dupont", email: "marie.dupont@example.com", telephone: "+32 470 12 34 56",
  modele: "Milan", couleur: "Blanc", sku: "MIL-WHT", quantite: 4, prix: "140,00",
  heure_debut: "10:00", heure_fin: "12:00", adresse: "Rue Neuve 12", code_postal: "1000", ville: "Bruxelles",
  details_commande: "4x Milan Blanc + 2x Milan Rouge",
  date_livraison: "15/02/2026", numero_livraison: "L-2026-042",
  date_rdv: "vendredi 14 février", heure_rdv: "14h30",
  numero_devis: "D-2026-018", montant: "489,00", validite_jours: 30,
  numero_facture: "F-2026-025", date_echeance: "28/02/2026",
  nom_societe: "TopCocoon", nom_livreur: "Karim", tel_livreur: "+32 470 99 99 99",
  lien_avis: "https://g.page/topcocoon/review",
};

export default function MessageTemplatesEditor() {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [current, setCurrent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ subject: "", body: "" });
  const bodyRef = useRef(null);
  const subjectRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/message-templates`, { headers: headers() });
      setTemplates(res.data);
      if (!selectedKey && res.data.length > 0) setSelectedKey(res.data[0].key);
    } catch { toast.error("Erreur chargement templates"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selectedKey) return;
    const t = templates.find(t => t.key === selectedKey);
    if (t) { setCurrent({ ...t }); setDirty(false); }
  }, [selectedKey, templates]);

  // Aperçu en direct
  useEffect(() => {
    if (!current) return;
    const render = (str) => {
      let out = str || "";
      Object.entries(SAMPLE_CONTEXT).forEach(([k, v]) => { out = out.replaceAll(`{${k}}`, v); });
      out = out.replace(/\{[a-z_]+\}/g, "");
      return out;
    };
    setPreview({ subject: render(current.subject), body: render(current.body) });
  }, [current]);

  const insertVariable = (field, varName) => {
    if (!current) return;
    const ref = field === "subject" ? subjectRef : bodyRef;
    const el = ref.current;
    const insert = `{${varName}}`;
    if (el) {
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;
      const before = (current[field] || "").slice(0, start);
      const after = (current[field] || "").slice(end);
      const newVal = before + insert + after;
      setCurrent({ ...current, [field]: newVal });
      setDirty(true);
      setTimeout(() => { el.focus(); el.setSelectionRange(start + insert.length, start + insert.length); }, 0);
    } else {
      setCurrent({ ...current, [field]: (current[field] || "") + insert });
      setDirty(true);
    }
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await axios.put(`${API}/message-templates/${current.key}`, {
        subject: current.subject || "", body: current.body || "",
      }, { headers: headers() });
      toast.success("Template enregistré");
      setDirty(false);
      load();
    } catch { toast.error("Erreur enregistrement"); }
    finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!current) return;
    if (!window.confirm(`Restaurer le template "${current.titre}" à sa valeur par défaut ?`)) return;
    try {
      await axios.post(`${API}/message-templates/${current.key}/reset`, {}, { headers: headers() });
      toast.success("Template réinitialisé");
      load();
    } catch { toast.error("Erreur reset"); }
  };

  const copyPreview = () => {
    const txt = current.type === "email" ? `${preview.subject}\n\n${preview.body}` : preview.body;
    navigator.clipboard.writeText(txt);
    toast.success("Aperçu copié");
  };

  return (
    <Card className="border-indigo-200" data-testid="message-templates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="w-5 h-5 text-indigo-600" />
          Modèles de messages (SMS & Emails)
        </CardTitle>
        <CardDescription>
          Personnalise les textes envoyés aux clients. Utilise les variables entre accolades (ex : <code className="bg-slate-100 px-1 rounded">{"{prenom}"}</code>) — elles sont remplacées automatiquement selon le contexte.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Colonne 1 : liste */}
          <div className="lg:col-span-1 space-y-1 max-h-[600px] overflow-y-auto">
            {templates.map(t => (
              <button
                key={t.key}
                type="button"
                onClick={() => setSelectedKey(t.key)}
                className={`w-full text-left px-3 py-2 rounded-lg border transition ${selectedKey === t.key ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                data-testid={`template-item-${t.key}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs">{t.type === "sms" ? "📞" : "📧"}</span>
                  <span className="text-sm font-medium">{t.titre.replace(/^(SMS|Email) — /, "")}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Colonne 2-3 : éditeur */}
          <div className="lg:col-span-2 space-y-3">
            {current ? (
              <>
                <div className="flex items-center justify-between">
                  <Badge className={current.type === "sms" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}>
                    {current.type === "sms" ? "📞 SMS" : "📧 Email"}
                  </Badge>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={resetToDefault} data-testid="tpl-reset-btn">
                      <RotateCcw className="w-3 h-3 mr-1" />Reset défaut
                    </Button>
                    <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="tpl-save-btn">
                      <Check className="w-3 h-3 mr-1" />{saving ? "..." : dirty ? "Enregistrer" : "Enregistré"}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500">{current.description}</p>

                {current.type === "email" && (
                  <div>
                    <Label className="text-xs font-semibold">Objet</Label>
                    <Input
                      ref={subjectRef}
                      value={current.subject || ""}
                      onChange={(e) => { setCurrent({ ...current, subject: e.target.value }); setDirty(true); }}
                      data-testid="tpl-subject-input"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-xs font-semibold">Corps du message</Label>
                  <Textarea
                    ref={bodyRef}
                    value={current.body || ""}
                    onChange={(e) => { setCurrent({ ...current, body: e.target.value }); setDirty(true); }}
                    rows={12}
                    className="font-mono text-sm"
                    data-testid="tpl-body-input"
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 italic">Sélectionne un template à gauche…</p>
            )}
          </div>

          {/* Colonne 4 : variables + aperçu */}
          <div className="lg:col-span-1 space-y-3">
            {current && (
              <>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Variables disponibles</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(current.variables || []).map(v => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => insertVariable("body", v)}
                        title={`Insère {${v}} dans le corps`}
                        className="text-xs px-2 py-1 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded font-mono text-indigo-800"
                        data-testid={`insert-var-${v}`}
                      >
                        {"{"}{v}{"}"}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Clique pour insérer au curseur.</p>
                </div>

                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold flex items-center gap-1"><Eye className="w-3 h-3" />Aperçu avec exemples</Label>
                    <button type="button" onClick={copyPreview} title="Copier" className="text-slate-500 hover:text-slate-700"><Copy className="w-3 h-3" /></button>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="tpl-preview">
                    {current.type === "email" && preview.subject && <div className="font-bold mb-2">Objet : {preview.subject}</div>}
                    {preview.body}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
